/**
 * Object Type Store - Manages object type definitions and mappings
 * Persists to localStorage: map-editor-object-types-{lotId}
 * Object types are now scoped per project (parking lot)
 *
 * API Integration: Syncs with backend for persistent storage
 */

import { create } from 'zustand';
import { storage } from '../../lib/utils';
import { loadInitialState, getStorageKey } from './persistence';
import { createTypeActions } from './typeActions';
import { createMappingActions } from './mappingActions';
import { createApiMethods } from './apiMethods';
import type { ObjectTypeState } from './types';

export type { Property, ObjectType, ObjectTypeKind, Mapping, LayerTypeMapping, ObjectTypeState } from './types';

export const useObjectTypeStore = create<ObjectTypeState>((set, get) => {
  const initialState = loadInitialState(null);

  return {
    currentLotId: null,
    currentProjectId: null,
    siteProjectId: null,
    isLoading: false,
    error: null,
    mappingsLoading: false,
    currentLoadingFloorId: null,
    ...initialState,

    setCurrentProjectId: (projectId) => {
      set({ currentProjectId: projectId, error: null });
    },

    setSiteProjectId: (projectId) => {
      set({ siteProjectId: projectId });
    },

    setCurrentLot: (lotId) => {
      const state = get();

      // Save current lot's data before switching
      if (state.currentLotId) {
        const currentState = {
          types: state.types,
          mappings: state.mappings,
          layerTypeMappings: state.layerTypeMappings,
        };
        storage.set(getStorageKey(state.currentLotId), currentState);
      }

      // Load new lot's data
      const newState = loadInitialState(lotId);

      set({
        currentLotId: lotId,
        types: newState.types,
        mappings: newState.mappings,
        layerTypeMappings: newState.layerTypeMappings,
      });
    },

    ...createTypeActions(set, get),
    ...createMappingActions(set, get),
    ...createApiMethods(set, get),
  };
});
